import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import apiService, { api } from '@/services/api'
import i18n from '@/i18n'
import type { User } from '@/types'

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref<User | null>(null)
  const token = ref<string | null>(null)
  const loading = ref(false)
  const selectedUserId = ref<number | null>(null)

  // Getters
  const isAuthenticated = computed(() => !!token.value && !!user.value)
  const isEmailVerified = computed(() => !!user.value?.email_verified_at)
  const currentUser = computed(() => user.value)
  const accessibleUsers = computed(() => {
    if (!user.value) return []
    const users = [user.value]
    if (user.value.dependents) {
      users.push(...user.value.dependents)
    }
    return users
  })
  const activeUserId = computed(() => selectedUserId.value || user.value?.id || null)

  // Helpers
  function syncLocale(userLocale?: string) {
    if (userLocale) {
      i18n.global.locale.value = userLocale
      localStorage.setItem('health-monitor-locale', userLocale)
      document.documentElement.lang = userLocale
    }
  }

  // Actions
  async function login(email: string, password: string) {
    loading.value = true
    try {
      const response = await api.post('/auth/login', { email, password })
      token.value = response.data.token
      user.value = response.data.user
      selectedUserId.value = response.data.user.id
      syncLocale(response.data.user.locale)
      return response.data
    } finally {
      loading.value = false
    }
  }

  async function register(data: {
    name: string
    email: string
    password: string
    password_confirmation: string
    phone?: string
    date_of_birth?: string
    gender?: string
    blood_group?: string
    locale?: string
  }) {
    loading.value = true
    try {
      const response = await api.post('/auth/register', data)
      token.value = response.data.token
      user.value = response.data.user
      selectedUserId.value = response.data.user.id
      syncLocale(response.data.user.locale)
      return response.data
    } finally {
      loading.value = false
    }
  }

  async function fetchUser() {
    if (!token.value) return null
    loading.value = true
    try {
      const response = await api.get('/auth/user')
      user.value = response.data.user
      if (!selectedUserId.value) {
        selectedUserId.value = response.data.user.id
      }
      syncLocale(response.data.user.locale)
      return response.data.user
    } finally {
      loading.value = false
    }
  }

  async function updateProfile(data: FormData | Partial<User>) {
    loading.value = true
    try {
      let response
      if (data instanceof FormData) {
        // Append _method to simulate PUT request with FormData
        data.append('_method', 'PUT')
        response = await apiService.users.updateProfile(data)
      } else {
        response = await api.put('/auth/user', data)
      }
      user.value = response.data.user
      return response.data
    } finally {
      loading.value = false
    }
  }

  async function updateProfilePhoto(photo: File) {
    const formData = new FormData()
    formData.append('profile_photo', photo)
    formData.append('_method', 'PUT')
    loading.value = true
    try {
      const response = await apiService.users.updateProfile(formData)
      user.value = response.data.user
      return response.data
    } finally {
      loading.value = false
    }
  }

  async function changePassword(data: {
    current_password: string
    password: string
    password_confirmation: string
  }) {
    loading.value = true
    try {
      const response = await apiService.users.changePassword(data)
      return response.data
    } finally {
      loading.value = false
    }
  }

  async function forgotPassword(email: string) {
    loading.value = true
    try {
      const response = await api.post('/auth/forgot-password', { email })
      return response.data
    } finally {
      loading.value = false
    }
  }

  async function logout() {
    try {
      if (token.value) {
        await api.post('/auth/logout')
      }
    } catch (error) {
      // Ignore errors during logout
    } finally {
      user.value = null
      token.value = null
      selectedUserId.value = null
    }
  }

  function selectUser(userId: number) {
    selectedUserId.value = userId
  }

  async function updateFcmToken(fcmToken: string) {
    try {
      await api.post('/notifications/fcm-token', { fcm_token: fcmToken })
    } catch (error) {
      console.error('Failed to update FCM token:', error)
    }
  }

  async function sendVerificationEmail() {
    loading.value = true
    try {
      const response = await api.post('/auth/email/send')
      return response.data
    } finally {
      loading.value = false
    }
  }

  async function verifyEmail(code: string) {
    loading.value = true
    try {
      const response = await api.post('/auth/email/verify', { code })
      user.value = response.data.user
      return response.data
    } finally {
      loading.value = false
    }
  }

  return {
    // State
    user,
    token,
    loading,
    selectedUserId,
    // Getters
    isAuthenticated,
    isEmailVerified,
    currentUser,
    accessibleUsers,
    activeUserId,
    // Actions
    login,
    register,
    fetchUser,
    updateProfile,
    updateProfilePhoto,
    changePassword,
    forgotPassword,
    logout,
    selectUser,
    updateFcmToken,
    sendVerificationEmail,
    verifyEmail,
  }
}, {
  persist: {
    key: 'health-monitor-auth',
    paths: ['token', 'selectedUserId'],
  },
})
